import 'react-virtualized/styles.css';

import React, {
  useCallback,
  useEffect,
  useState,
  memo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';

import { AutoSizer, List, WindowScroller } from 'react-virtualized';
import styled from 'styled-components';

import Alert from '@mui/lab/Alert';
import Tooltip from '@mui/material/Tooltip';
import CheckboxOriginal from 'shared/components/Checkbox';
import Skeleton from '@mui/lab/Skeleton';

import get from 'lodash/get';
import castArray from 'lodash/castArray';
import {
  columnTypes,
  defaultColDef as defaultColDefBase,
} from 'shared/components/tables';
import TableHeader, { CardViewHeader } from 'shared/components/TableHeader';
import TrueImpactBadge from 'shared/design-system/components/TrueImpactBadge';

const defaultNoData = 'There is no data matching your requirements';
const noDataForFilters = 'There is no data matching your filters';

const VirtualizedView = forwardRef(
  (
    {
      groups,
      view = 'grid',
      columns: columns0,
      onRenderGroupHeader,
      onRenderRow,
      onGetRowHeight,
      onSelectionChanged,
      onExpansionChanged,
      noDataText = defaultNoData,
      isLoading,
      onFilterValue,
      unfilteredGroups,
      showMoreFilters,
      defaultSortByColumn,
      onHeaderClick,
      tableExtraPadding = 0,
    },
    ref,
  ) => {
    const {
      flatGroups,
      setExpandedGroups: originalSetExpandedGroups,
      selectedRows,
      setSelectedRows,
      flatSelectedRows,
      flatGroupsForSelection,
    } = useGroups(groups, onSelectionChanged, !!onRenderGroupHeader);
    const setExpandedGroups = useCallback(
      value => {
        originalSetExpandedGroups(oldValue => {
          if (typeof value === 'function') {
            value = value(oldValue);
          }
          if (onExpansionChanged) {
            onExpansionChanged(
              Object.fromEntries(
                Object.entries(value).filter(([, isExpanded]) => isExpanded),
              ),
            );
          }
          return value;
        });
      },
      [originalSetExpandedGroups, onExpansionChanged],
    );
    useImperativeHandle(ref, () => ({
      clearSelection() {
        setSelectedRows({});
      },
      expandAll() {
        setExpandedGroups(
          Object.fromEntries(groups.map(({ key }) => [key, true])),
        );
      },
      collapseAll() {
        setExpandedGroups({});
      },
    }));

    const [sortByColumn, setSortByColumn] = useState(defaultSortByColumn);
    const [sortDirection, setSortDirection] = useState(
      defaultSortByColumn ? 0 : undefined,
    );
    const columnForSort = useMemo(
      () => columns0?.find(({ headerName }) => headerName === sortByColumn),
      [columns0, sortByColumn],
    );
    const sortedRows = useMemo(
      () => sortGroups({ columnForSort, flatGroups, sortDirection }),
      [columnForSort, flatGroups, sortDirection],
    );
    const handleGetRowData = useCallback(index => sortedRows?.[index], [
      sortedRows,
    ]);

    const list = useRef();
    useEffect(() => {
      if (list.current) {
        list.current.recomputeRowHeights();
      }
    }, [list, view]);
    const handleRowHeight = useCallback(
      ({ index }) => {
        if (index === 0) {
          return 24;
        }
        if (onGetRowHeight) {
          return onGetRowHeight({
            index: index - 1,
            data: flatGroups[index - 1],
          });
        }
        const isLastRow =
          handleGetRowData(index)?.groupHeader !== undefined ||
          !handleGetRowData(index);
        return flatGroups[index - 1]?.groupHeader === undefined
          ? (view === 'card' ? 80 : 45) + (isLastRow ? 10 : 0)
          : 46;
      },
      [flatGroups, view, onGetRowHeight, handleGetRowData],
    );

    const checked =
      flatSelectedRows?.length ===
      flatGroupsForSelection?.filter(
        ({ groupHeader }) => groupHeader === undefined,
      )?.length;
    const indeterminate = !checked && flatSelectedRows?.length > 0;

    const [columns, setColumns] = useState(columns0);
    const totalColumnWidth = columns0
      ?.filter(column => column)
      ?.reduce((total, { width }) => total + (width || 0), 0);
    const numFlexColumns = columns0
      ?.filter(column => column)
      ?.reduce((total, { width }) => total + (width ? 0 : 1), 0);
    const [tableWidth, setTableWidth] = useState(
      numFlexColumns ? window.innerWidth - windowWidthGap : totalColumnWidth,
    );
    useEffect(() => {
      if (!columns0) {
        return;
      }

      setColumns(
        columns0
          .filter(column => column)
          .map(column => ({
            tooltipValueGetter: ({ value }) => value,
            ...column,
            width:
              column.width ??
              (tableWidth - tableExtraPadding - totalColumnWidth - 10) /
                numFlexColumns,
          }))
          .map(({ headerName, width, cellStyle, ...other }) => ({
            headerName,
            width,
            ...other,
            // filter: false,
            cellStyle: cellStyle ?? {
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maxWidth: width,
              maxHeight: 24,
              padding: '0 8px',
            },
          })),
      );
    }, [columns0, tableWidth, tableExtraPadding]);

    const handleSelectAll = useCallback(() => {
      if (checked) {
        setSelectedRows({});
      } else {
        if (onRenderGroupHeader) {
          setSelectedRows(
            Object.fromEntries(groups.map(({ key, values }) => [key, values])),
          );
        } else {
          setSelectedRows({
            ungrouped: groups,
          });
        }
      }
    }, [groups, checked]);

    const Row = useCallback(memo(onRenderRow), [onRenderRow]);
    const Header = useCallback(memo(onRenderGroupHeader), [
      onRenderGroupHeader,
    ]);
    const hasCheckBox = Boolean(onSelectionChanged);
    const rowRenderer = useCallback(
      ({
        //key: _, // Unique key within array of rows
        index, // Index of row within collection
        isScrolling, // The List is currently being scrolled
        isVisible, // This row is visible within the List (eg it is not an overscanned row)
        style, // Style object to be applied to row (to position it)
        parent,
      }) => {
        if (!index) {
          return null;
        }
        index--;

        const value = handleGetRowData(index);
        if (!value) {
          return null;
        }
        const isLastRow =
          handleGetRowData(index + 1)?.groupHeader !== undefined ||
          !handleGetRowData(index + 1);

        if (value.groupHeader === undefined) {
          return (
            <Row
              style={style}
              value={value}
              columns={columns}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              hasCheckBox={hasCheckBox}
              isLastRow={isLastRow}
              index={index}
            />
          );
        }

        return (
          <Header
            style={style}
            value={value}
            columns={columns}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            hasCheckBox={hasCheckBox}
            onExpandCollapse={() => {
              setExpandedGroups(oldGroups => ({
                ...oldGroups,
                [value.groupHeader]: !oldGroups[value.groupHeader],
              }));
            }}
          />
        );
      },
      [handleGetRowData, Row, Header, selectedRows, setSelectedRows],
    );
    const handleResize = useCallback(() => {
      if (numFlexColumns) {
        setTableWidth(window.innerWidth - windowWidthGap);
      }
    }, [numFlexColumns]);
    const handleHeaderClick = useCallback(
      (column, event) => {
        if (column.sortable) {
          setSortByColumn(oldColumn => {
            if (oldColumn === column.headerName) {
              setSortDirection((oldDirection = 0) => {
                return 1 - oldDirection;
              });
            } else {
              setSortDirection(0);
            }
            return column?.headerName;
          });
        }
        if (onHeaderClick) {
          onHeaderClick(column, event);
        }
      },
      [onHeaderClick],
    );

    const isNoData = !isLoading && flatGroups?.length === 0;
    return (
      <>
        {!isNoData && (
          <WindowScroller>
            {({ height, scrollTop, isScrolling, onChildScroll }) => (
              <AutoSizer disableHeight style={{ maxWidth: '100vw' }}>
                {({ width }) => (
                  <Root
                    width={Math.min(width, tableWidth) - 16}
                    scrollTop={scrollTop}
                  >
                    {(onFilterValue || flatGroups?.length > 0 || isLoading) &&
                      view === 'grid' && (
                        <TableHeader
                          columns={columns}
                          scrollTop={scrollTop}
                          checked={checked}
                          indeterminate={indeterminate}
                          handleSelectAll={handleSelectAll}
                          hasCheckBox={hasCheckBox}
                          onFilterValue={onFilterValue}
                          sortByColumn={sortByColumn}
                          sortDirection={sortDirection}
                          rows={unfilteredGroups}
                          showMoreFilters={showMoreFilters}
                          onHeaderClick={handleHeaderClick}
                        />
                      )}
                    {(flatGroups?.length > 0 || isLoading) &&
                      view === 'card' && (
                        <CardViewHeader
                          scrollTop={scrollTop}
                          checked={checked}
                          indeterminate={indeterminate}
                          handleSelectAll={handleSelectAll}
                          hasCheckBox={hasCheckBox}
                        />
                      )}
                    <List
                      style={{ paddingTop: '20px' }}
                      ref={list}
                      autoHeight
                      rowCount={flatGroups ? flatGroups.length + 2 : 0}
                      rowHeight={handleRowHeight}
                      width={tableWidth - 16}
                      rowRenderer={rowRenderer}
                      scrollTop={scrollTop}
                      height={height}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                    />
                    <ValueLink value={width} onChange={handleResize} />
                    {isLoading && (
                      <>
                        <Placeholder
                          variant="rect"
                          width={tableWidth}
                          height={24}
                        />
                        <Placeholder
                          variant="rect"
                          width={tableWidth}
                          height={40}
                        />
                        <Placeholder
                          variant="rect"
                          width={tableWidth}
                          height={40}
                        />
                        <Placeholder
                          variant="rect"
                          width={tableWidth}
                          height={40}
                        />
                      </>
                    )}
                  </Root>
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
        {isNoData && (
          <>
            <AlertSpacer />
            <Alert severity="info">{noDataText}</Alert>
          </>
        )}
      </>
    );
  },
);

const defaultColDef = {
  ...defaultColDefBase,
  //  minWidth: 100,
  resizable: true,
};

function getCellValue({ data, field, valueGetter, valueFormatter }) {
  let value;

  if (field) {
    value = get(data, field);
  }
  if (valueGetter) {
    value = valueGetter({ data, value });
  }
  if (valueFormatter) {
    value = valueFormatter({ data, value });
  }

  return value;
}

function renderCell({
  data,
  field,
  valueGetter,
  valueFormatter,
  cellRenderer,
}) {
  let value = getCellValue({ data, field, valueGetter, valueFormatter });
  if (cellRenderer) {
    const rendered = cellRenderer({ data, value });
    value =
      typeof rendered === 'string' ? (
        <div dangerouslySetInnerHTML={{ __html: rendered }} />
      ) : (
        rendered
      );
  }

  return value;
}

const TableCell = styled.div`
  height: 100%;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  min-width: ${({ width }) => width}px;
  ${({ onClick }) =>
    !!onClick &&
    `
    &:hover {
      text-decoration: underline;
      cursor: pointer;
    }
  `}
`;

const defaultKeyField = ['allocationId', 'investmentId']; // TODO: put this at Home.js

export function RowGrid({
  value,
  selectedRows,
  setSelectedRows,
  columns,
  style,
  columnTypes,
  groupKeyField,
  onExpandCollapse,
  hasCheckBox,
  onRowClicked,
  isLastRow,
  keyField = defaultKeyField,
  children,
  ...other
}) {
  keyField = castArray(keyField);

  const groupKey =
    value?.groupHeader ?? get(value, groupKeyField) ?? 'ungrouped';
  const isGroup = value?.groupHeader !== undefined;
  const checked = isGroup
    ? selectedRows?.[groupKey]?.length === value?.values?.length
    : selectedRows?.[groupKey]?.some(row =>
        keyField.every(field => get(row, field) === get(value, field)),
      ) ?? false;
  const handleChangeCheckbox = useCallback(
    e => {
      setSelectedRows(oldSelection => {
        if (isGroup) {
          return {
            ...oldSelection,
            [groupKey]: !checked ? value?.values : [],
          };
        }

        const selected = oldSelection[groupKey] ?? [];
        return {
          ...oldSelection,
          [groupKey]: !checked
            ? [...selected, value]
            : selected.filter(
                row =>
                  !keyField.every(
                    field => get(row, field) === get(value, field),
                  ),
              ),
        };
      });
    },
    [setSelectedRows, value, checked],
  );

  let colSpanCount = 0;
  const processedColumns = [];
  const columns1 = columns.map(col =>
    Array.isArray(col.type)
      ? {
          ...col.type.reduce(
            (all, colType) => ({ ...all, ...columnTypes[colType] }),
            {},
          ),
          ...col,
        }
      : col,
  );

  for (const column of columns1) {
    const { field, valueGetter, valueFormatter, colSpan } = column;
    const cellValue = getCellValue({
      data: value,
      field,
      valueGetter,
      valueFormatter,
    });

    if (colSpanCount > 0) {
      const spanned = processedColumns[processedColumns.length - 1];
      spanned.width += column.width;
      colSpanCount--;
      continue;
    }

    if (colSpan) {
      colSpanCount = colSpan({ data: value, column, value: cellValue }) - 1;
      processedColumns.push({ ...column });
    } else {
      processedColumns.push(column);
    }
  }

  const checkboxStyle = useMemo(
    () => ({
      color: '#F89915 !important',
    }),
    [],
  );

  return (
    <RowGridContainer
      style={style}
      isLastRow={isLastRow}
      checked={checked}
      hasGroups={false}
      {...other}
    >
      {hasCheckBox && (
        <Checkbox
          checked={checked}
          style={checkboxStyle}
          onChange={handleChangeCheckbox}
          indent={4}
        />
      )}
      {processedColumns.map((column, index) => {
        const {
          headerName,
          field,
          width,
          valueGetter,
          valueFormatter,
          cellRenderer,
          colSpan,
          onCellClicked = onRowClicked,
          tooltipValueGetter,
        } = column;
        const cellValue = getCellValue({
          data: value,
          field,
          valueGetter,
          valueFormatter,
        });
        if (colSpan) {
          colSpanCount = colSpan({ data: value, column, value: cellValue }) - 1;
        }

        const cellValueFinal = renderCell({
          data: value,
          field,
          valueGetter,
          valueFormatter,
          cellRenderer,
        });

        return (
          <TableCell
            className={`${headerName}-${field}`}
            key={`${headerName}-${field}`}
            width={width}
            onClick={
              onExpandCollapse && value.groupHeader !== undefined && index === 0
                ? onExpandCollapse
                : onCellClicked
                ? event =>
                    onCellClicked({
                      event,
                      data: value,
                      column,
                      value: cellValue,
                    })
                : null
            }
          >
            <CellRow>
              {/*                {typeof(isExpanded) === 'boolean' && index == 0
                  ? isExpanded ? <ChevronLeft/> : <ChevronRight/>
                  : null}*/}
              <OptionalTooltip
                title={
                  typeof cellValue !== 'object' && tooltipValueGetter
                    ? tooltipValueGetter({ data: value, value: cellValue })
                    : undefined
                }
              >
                <EllipsisText>{cellValueFinal}</EllipsisText>
              </OptionalTooltip>
              {field === 'programName' && value?.v3_flag && <TrueImpactBadge>Legacy</TrueImpactBadge>}
            </CellRow>
          </TableCell>
        );
      })}
      {children}
    </RowGridContainer>
  );
}

const OptionalTooltip = ({ title, children }) =>
  title ? (
    <Tooltip {...{ title }} interactive>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );

const ValueLink = ({ value, onChange }) => {
  useEffect(() => {
    onChange(value);
  }, [onChange, value]);
  return null;
};

export function useGroups(groups, onSelectionChanged, hasGroups) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedRows, setSelectedRows0] = useState({});
  const setSelectedRows = useCallback(
    value => {
      setSelectedRows0(oldValue => {
        if (typeof value === 'function') {
          value = value(oldValue);
        }
        if (onSelectionChanged) {
          if (hasGroups) {
            onSelectionChanged(value);
          } else {
            onSelectionChanged(value.ungrouped);
          }
        }
        return value;
      });
    },
    [setSelectedRows0, onSelectionChanged],
  );

  let rowIndex = 0;
  const flatGroups = hasGroups
    ? groups?.reduce(
        (all, { key, values }) => [
          ...all,
          {
            rowIndex: rowIndex++,
            groupHeader: key,
            isExpanded: expandedGroups[key] ?? false,
            values,
          },
          ...(expandedGroups[key] ? values : []).map(row => ({
            ...row,
            rowIndex: rowIndex++,
          })),
        ],
        [],
      )
    : groups;
  const flatGroupsForSelection = hasGroups
    ? groups?.reduce(
        (all, { key, values }) => [
          ...all,
          {
            groupHeader: key,
            values,
          },
          ...values,
        ],
        [],
      )
    : groups;
  const flatSelectedRows =
    selectedRows &&
    Object.entries(selectedRows).reduce(
      (all, [, values]) => [...all, ...values],
      [],
    );

  return {
    flatGroups,
    flatGroupsForSelection,
    setExpandedGroups,
    selectedRows,
    setSelectedRows,
    flatSelectedRows,
  };
}

const Checkbox = styled(CheckboxOriginal)`
  margin-left: 8px !important;
  margin-right: 8px !important;
`;

const RowGridContainer = styled.div`
  margin-top: -1px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: calc(100% - ${({ horizontalPadding = 40 }) =>
    horizontalPadding}px) !important;
  height: 65px !important;
  border-radius: 4px;
  ${({ checked }) => (checked ? 'background-color: #F5F8FA;' : '')}
  background-color: ${({ index }) => (index % 2 ? '#ebf0f7' : '#f6f8fa')};
  padding: 0 4px;

  &:hover {
    background-color: #EAF0F6;
  }
  ${({ isLastRow, hasGroups }) =>
    hasGroups
      ? isLastRow
        ? `box-shadow: 0 4px 4px 0 #ccc;
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        padding-bottom: 10px;`
        : 'box-shadow: 0 4px 4px 0 #888;'
      : 'border: 1px solid #CBD6E2; margin-bottom: 5px;'}
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 40px;
    right: 16px;
    height: 1px;
    background-color: rgba(0,0,0,.1);
  }
  .MuiCheckbox-root {
    color: #CBD6E2 !important;
  }
  .Impacts-impacts {
    width: auto !important;
    min-width: auto !important;
  }
`;

//border-top: 1px solid rgba(0,0,0,.1);

const CellRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const EllipsisText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Root = styled.div`
  position: relative;
  width: ${({ width }) => width}px;
  background-color: #f9f9f9;
`;

const Placeholder = styled(Skeleton)`
  margin-bottom: 5px;
`;

const AlertSpacer = styled.div`
  height: 30px;
`;

const windowWidthGap = 16;

function compareValues(a, b) {
  if (typeof a === 'number') {
    return a - b;
  }
  if (typeof a === 'string') {
    return (a ?? '').localeCompare(b);
  }
  return 0;
}

function sortGroups({ columnForSort, flatGroups, sortDirection }) {
  if (!columnForSort || !flatGroups) {
    return flatGroups;
  }
  let result = [];

  for (let start = 0; start < flatGroups.length; ) {
    let end;
    if (flatGroups[start].groupHeader === undefined) {
      end = flatGroups.length;
    } else {
      result.push(flatGroups[start]);
      for (
        end = start + 1;
        end < flatGroups.length && flatGroups[end].groupHeader === undefined;
        end++
      );
    }

    let sortedGroup =
      end > start + 1
        ? flatGroups
            ?.slice(start + 1, end)
            ?.sort((a, b) =>
              compareValues(
                getCellValue({ data: a, ...columnForSort }),
                getCellValue({ data: b, ...columnForSort }),
              ),
            )
        : [];

    if (sortDirection) {
      sortedGroup = sortedGroup.reverse();
    }

    result = result.concat(sortedGroup);
    start = end;
  }

  return result;
}

export default VirtualizedView;
